import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Drawer,
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Input, 
  Select, 
  Typography,
  Alert,
  Radio,
  Divider
} from 'antd';
import { 
  DeleteOutlined,
  UserOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { FiEdit, FiTrash2, FiSave, FiX, FiEye } from 'react-icons/fi';
import { ResetIcon, RefreshIcon } from '../common/ActionIcons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { 
  listPatientsByIds, 
  deletePatient, 
  updatePatient, 
  loadPatientDto 
} from '../../actions/patientActions';
import { showApiError } from '../../utils/formatApiError';
import ApiErrorAlert from '../../components/common/ApiErrorAlert';

const { Text } = Typography;

// Compact Table Title component similar to SampleTable
const PatientTableTitle = ({
  filter,
  setFilter,
  searchText,
  setSearchText,
  searchProp,
  setSearchProp,
  selectedRowKeys,
  handleBulkDelete,
  isBulkDeleting,
  onRefresh,
  statusFilter,
  setStatusFilter,
  t
}) => {
  const searchOptions = [
    { value: 'patientIdentifier', label: t('Patient ID') },
    { value: 'nhsNumber', label: t('NHS Number') },
    { value: 'firstName', label: t('First Name') },
    { value: 'lastName', label: t('Last Name') },
    { value: 'postcode', label: t('Postcode') },
    { value: 'batchName', label: t('Batch Name') }
  ];

  const statusOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Approved'), value: 'APPROVED' },
    { label: t('Rejected'), value: 'REJECTED' },
    { label: t('Pending Review'), value: 'PENDING_REVIEW' }
  ];

  const handleSearchPropChange = (value) => {
    setSearchProp(value);
    setFilter(prev => ({ ...prev, filter: value, query: searchText, page: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilter(prev => ({ ...prev, filter: searchProp, query: value, page: 1 }));
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setFilter(prev => ({ 
      ...prev, 
      filter: 'status',
      query: value === 'all' ? '' : value,
      page: 1 
    }));
  };

  const handleReset = () => {
    setSearchText('');
    setSearchProp('patientIdentifier');
    setStatusFilter('all');
    setFilter({
      query: '',
      filter: 'patientIdentifier',
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  return (
    <React.Fragment>
      <Space wrap style={{ marginBottom: '12px' }}>
        <span style={{ fontSize: '12px', color: '#666' }}>{t('Filter')}:</span>
        <Select
          placeholder={t("Search by")}
          style={{ width: '140px' }}
          size="small"
          value={searchProp}
          onChange={handleSearchPropChange}
        >
          {searchOptions.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Input.Search
          placeholder={`${t("Search by")} ${searchOptions.find(opt => opt.value === searchProp)?.label || ''}`}
          onSearch={handleSearch}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ minWidth: '200px' }}
          size="small"
        />
        <Button
          type="default"
          icon={<ResetIcon />}
          onClick={handleReset}
          size="small"
        >
          {t("Reset")}
        </Button>
        <Button
          icon={<RefreshIcon />}
          onClick={onRefresh}
          size="small"
        >
          {t('Refresh')}
        </Button>
      </Space>

      {/* Status Filter */}
      <div style={{ marginBottom: '12px' }}>
        <Text style={{ fontSize: '12px', color: '#666', marginRight: '8px' }}>{t('Status')}:</Text>
        <Radio.Group
          value={statusFilter}
          onChange={handleStatusFilterChange}
          size="small"
          buttonStyle="solid"
        >
          {statusOptions.map((option) => (
            <Radio.Button key={option.value} value={option.value}>
              {option.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {selectedRowKeys.length > 0 && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
            <Popconfirm
              title={t("Delete selected items")}
              description={t("Are you sure you want to delete these patients?")}
              okText={t("Yes")}
              cancelText={t("No")}
              onConfirm={handleBulkDelete}
            >
              <Button type="primary" danger loading={isBulkDeleting} size="small">
                {t("Delete")} ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          </div>
        </>
      )}
    </React.Fragment>
  );
};

const PatientListDrawer = ({ 
  visible, 
  onClose, 
  patientIds = [], 
  title = "Patients"
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const patientListByIds = useSelector(state => state.patientListByIds);
  const { loading: loadingList, patients = [], error: errorList, totalCount } = patientListByIds;

  const patientDelete = useSelector(state => state.patientDelete);
  const { loading: loadingDelete, success: successDelete, error: errorDelete } = patientDelete;

  const patientUpdate = useSelector(state => state.patientUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate, patient: updatedPatient } = patientUpdate;

  // Local state
  const [filter, setFilter] = useState({
    query: '',
    filter: 'patientIdentifier',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [searchText, setSearchText] = useState('');
  const [searchProp, setSearchProp] = useState('patientIdentifier');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  
  // Local state to track patients for immediate updates
  const [localPatients, setLocalPatients] = useState([]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!visible) {
      dispatch({ type: 'PATIENT_LIST_BY_IDS_RESET' });
      setLocalPatients([]);
      setEditingRowId(null);
      setEditingData({});
      setSelectedRowKeys([]);
    }
  }, [visible, dispatch]);

  // Update local patients when Redux patients change
  useEffect(() => {
    if (patients) {
      setLocalPatients(patients);
    }
  }, [patients]);

  // Load patients when drawer opens and IDs change
  useEffect(() => {
    if (visible && patientIds && patientIds.length > 0) {
      dispatch(listPatientsByIds(patientIds));
    }
  }, [dispatch, visible, patientIds]);

  // Handle successful operations
  useEffect(() => {
    if (successDelete) {
      message.success(t('Patient deleted successfully'));
      if (patientIds && patientIds.length > 0) {
        dispatch(listPatientsByIds(patientIds));
      }
      dispatch({ type: 'PATIENT_DELETE_RESET' });
    }
  }, [successDelete, dispatch, patientIds, t]);

  // Handle update success
  useEffect(() => {
    if (successUpdate && editingRowId && updatedPatient) {
      setLocalPatients(prevPatients => 
        prevPatients.map(patient => 
          patient.id === updatedPatient.id ? { ...patient, ...updatedPatient } : patient
        )
      );
      message.success(t('Patient updated successfully'));
      setEditingRowId(null);
      setEditingData({});
      dispatch({ type: 'PATIENT_UPDATE_RESET' });
    }
  }, [successUpdate, editingRowId, t, updatedPatient]);

  // Handle errors
  useEffect(() => {
    if (errorUpdate && editingRowId) {
      message.error(Array.isArray(errorUpdate) ? errorUpdate.join(', ') : errorUpdate);
    }
    if (errorDelete) {
      showApiError(errorDelete, t);
      dispatch({ type: 'PATIENT_DELETE_RESET' });
    }
  }, [errorUpdate, errorDelete, editingRowId, t]);

  // Filter local patients based on status filter and search
  const filteredLocalPatients = React.useMemo(() => {
    // Don't filter locally - let the backend handle all filtering
    return localPatients;
  }, [localPatients]);

  // Handlers
  const handleRefresh = useCallback(() => {
    if (patientIds && patientIds.length > 0) {
      dispatch(listPatientsByIds(patientIds));
    }
  }, [dispatch, patientIds]);

  const handleDelete = useCallback((id) => {
    dispatch(deletePatient(id));
  }, [dispatch]);

  const handleBulkDelete = useCallback(async () => {
    setIsBulkDeleting(true);
    try {
      for (const key of selectedRowKeys) {
        dispatch(deletePatient(key));
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      message.success(t('Selected patients deleted successfully'));
    } catch (error) {
      message.error(t('Failed to delete some patients. Please try again.'));
    } finally {
      setSelectedRowKeys([]);
      setIsBulkDeleting(false);
    }
  }, [selectedRowKeys, dispatch, t]);

  const handleEdit = useCallback((record) => {
    setEditingRowId(record.id);
    setEditingData({ ...record });
  }, []);

  const handleSave = useCallback(() => {
    if (editingRowId && editingData) {
      const currentPatient = localPatients.find(p => p.id === editingRowId);
      const changes = {};
      Object.keys(editingData).forEach(key => {
        if (editingData[key] !== currentPatient[key]) {
          changes[key] = editingData[key];
        }
      });

      if (Object.keys(changes).length > 0) {
        dispatch(updatePatient(editingRowId, changes));
      } else {
        setEditingRowId(null);
        setEditingData({});
      }
    }
  }, [editingRowId, editingData, localPatients, dispatch]);

  const handleCancel = useCallback(() => {
    setEditingRowId(null);
    setEditingData({});
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handlePatientIdClick = useCallback((patientId, e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    navigate(`/patients/${patientId}`);
  }, [navigate]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setFilter(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      sortBy: sorter.field || 'createdAt',
      sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
    }));
  }, []);

  // Helper functions
  const getAgeFromDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) return '-';
    return dayjs().diff(dayjs(dateOfBirth), 'year');
  };

  const getStatusTag = (status) => {
    const statusColors = {
      'APPROVED': 'green',
      'REJECTED': 'red',
      'PENDING': 'orange',
      'PENDING_REVIEW': 'orange'
    };
    
    return (
      <Tag color={statusColors[status] || 'default'}>
        {status}
      </Tag>
    );
  };

  // Table columns - simplified for drawer view
  const columns = [
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'left',
      width: 80,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        return (
          <Space size="small">
            {isEditing ? (
              <>
                <Button type="primary" size="small" icon={<FiSave />} onClick={handleSave} loading={loadingUpdate} />
                <Button size="small" icon={<FiX />} onClick={handleCancel} />
              </>
            ) : (
              <>
                <Button type="text" size="small" icon={<FiEdit />} onClick={() => handleEdit(record)} />
                <Popconfirm
                  title={t('Delete Patient')}
                  onConfirm={() => handleDelete(record.id)}
                  okText={t('Yes')}
                  cancelText={t('No')}
                >
                  <Button type="text" size="small" icon={<FiTrash2 />} danger loading={loadingDelete} />
                </Popconfirm>
              </>
            )}
          </Space>
        );
      }
    },
    {
      title: t('Patient ID'),
      key: 'patient',
      width: 180,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        return (
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 6 }}>
              {isEditing ? (
                <Input
                  value={editingData.patientIdentifier || record.patientIdentifier}
                  onChange={(e) => handleInputChange('patientIdentifier', e.target.value)}
                  size="small"
                  placeholder="Patient ID"
                />
              ) : (
                <>
                  <span 
                    onClick={(e) => handlePatientIdClick(record.id, e)}
                    style={{ 
                      cursor: 'pointer',
                      color: '#1890ff',
                      textDecoration: 'none',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    <UserOutlined /> {record.patientIdentifier}
                  </span>
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    title={t('View patient details')}
                    onClick={(e) => handlePatientIdClick(record.id, e)}
                  />
                </>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
              NHS: {record.nhsNumber || '-'}
            </div>
            {(record.firstName || record.lastName) && (
              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                {record.firstName} {record.lastName}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: t('Age'),
      key: 'age',
      width: 80,
      render: (_, record) => {
        const age = getAgeFromDateOfBirth(record.dateOfBirth);
        return (
          <div style={{ fontSize: '12px' }}>
            <Text strong>{age}</Text>
          </div>
        );
      }
    },
    {
      title: t('DOB'),
      key: 'dateOfBirth',
      width: 100,
      render: (_, record) => {
        return (
          <div style={{ fontSize: '12px' }}>
            {record.dateOfBirth ? dayjs(record.dateOfBirth).format('DD/MM/YY') : '-'}
          </div>
        );
      }
    },
    {
      title: t('Gender'),
      key: 'gender',
      width: 80,
      render: (_, record) => {
        return (
          <div style={{ fontSize: '12px' }}>
            <div>{record.gender || '-'}</div>
            {record.dateOfDeath && (
              <div style={{ color: '#f5222d', fontSize: '10px' }}>
                Deceased: {dayjs(record.dateOfDeath).format('DD/MM/YY')}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: t('Postcode'),
      key: 'postcode',
      width: 100,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        
        return (
          <div style={{ fontSize: '12px' }}>
            {isEditing ? (
              <Input
                value={editingData.postcode || record.postcode}
                onChange={(e) => handleInputChange('postcode', e.target.value)}
                size="small"
                placeholder="Postcode"
              />
            ) : (
              record.postcode || '-'
            )}
          </div>
        );
      }
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.status || text}
            onChange={(value) => handleInputChange('status', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="APPROVED">APPROVED</Select.Option>
            <Select.Option value="REJECTED">REJECTED</Select.Option>
            <Select.Option value="PENDING">PENDING</Select.Option>
            <Select.Option value="REVIEW">REVIEW</Select.Option>
          </Select>
        ) : (
          getStatusTag(text)
        );
      }
    },
    {
      title: t('Status Reason'),
      dataIndex: 'statusReason',
      key: 'statusReason',
      width: 150,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Input.TextArea
            value={editingData.statusReason || text}
            onChange={(e) => handleInputChange('statusReason', e.target.value)}
            size="small"
            rows={2}
            placeholder="Status reason"
          />
        ) : (
          <Text style={{ fontSize: '12px' }}>{text || '-'}</Text>
        );
      }
    },
    {
      title: t('Batch'),
      key: 'batch',
      width: 120,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div><Text strong>Batch:</Text> {record.batchName || '-'}</div>
          <div><Text strong>Upload:</Text> {record.uploadBatchId ? record.uploadBatchId.substring(0, 8) + '...' : '-'}</div>
        </div>
      )
    },
    {
      title: t('Processed'),
      dataIndex: 'processedAt',
      key: 'processedAt',
      width: 130,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {date ? dayjs(date).format('DD/MM/YY HH:mm') : '-'}
        </Text>
      )
    },
    {
      title: t('Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {dayjs(date).format('DD/MM/YY HH:mm')}
        </Text>
      )
    }
  ];

  return (
    <Drawer
      title={
        <Space>
          <Text strong style={{ fontSize: '16px' }}>{title}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            ({filteredLocalPatients?.length || 0} {t('items')})
          </Text>
        </Space>
      }
      width={1200}
      open={visible}
      onClose={onClose}
      styles={{
        body: { padding: 0 },
        header: { 
          borderBottom: '1px solid #f0f0f0',
          padding: '16px 24px'
        }
      }}
    >
      <div style={{ padding: '16px 24px' }}>
        <ApiErrorAlert error={errorList} closable />

        <Table
          columns={columns}
          dataSource={filteredLocalPatients}
          rowKey="id"
          loading={loadingList}
          size="small"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
          }}
          pagination={{
            current: filter.page,
            pageSize: filter.limit,
            total: totalCount || 0,
            showSizeChanger: true,
            showQuickJumper: (totalCount || 0) > 50,
            showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
            pageSizeOptions: ['10', '20', '50'],
            size: 'small',
            position: ['bottomCenter']
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200, y: '70vh' }}
          bordered
          title={() => (
            <PatientTableTitle
              filter={filter}
              setFilter={setFilter}
              searchText={searchText}
              setSearchText={setSearchText}
              searchProp={searchProp}
              setSearchProp={setSearchProp}
              selectedRowKeys={selectedRowKeys}
              handleBulkDelete={handleBulkDelete}
              isBulkDeleting={isBulkDeleting}
              onRefresh={handleRefresh}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              t={t}
            />
          )}
        />
      </div>
    </Drawer>
  );
};

export default PatientListDrawer;
